<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import {
  DxDataGrid,
  DxColumn,
  DxScrolling,
  DxButton as DxGridButton,
} from 'devextreme-vue/data-grid';
import { DxPopup, DxToolbarItem } from 'devextreme-vue/popup';
import { DxButton } from 'devextreme-vue/button';
import { DxLoadPanel } from 'devextreme-vue/load-panel';
import { DxSelectBox } from 'devextreme-vue/select-box';
import { DxNumberBox } from 'devextreme-vue/number-box';
import { bottoGridPaging, showError, showSuccess } from '@botto/core';
import {
  createTransition,
  createTransitionStep,
  deleteTransition,
  deleteTransitionStep,
  fetchGoals,
  listTransitionSteps,
  listTransitions,
  projectsAdapter,
  updateTransition,
  updateTransitionStep,
  type Project,
  type Transition,
  type TransitionStep,
} from '../../api';
import SendSmsStepForm from './SendSmsStepForm.vue';
import {
  defaultSendSmsStep,
  formatStepSettings,
  parseSendSmsStep,
  toStepPayload,
  type SendSmsStepFormState,
} from './sendSmsSettings';

const props = defineProps<{
  projectId: string | null;
}>();

const { paging, pager } = bottoGridPaging();

const loading = ref(false);
const transitions = ref<Transition[]>([]);
const project = ref<Project | null>(null);
const goalItems = ref<string[]>([]);

const transitionPopupOpen = ref(false);
const transitionMode = ref<'create' | 'edit'>('create');
const transitionForm = ref({
  id: '',
  src_goal: '',
  dst_goal: '',
  schedule_interval: 60,
});

const stepsOpen = ref(false);
const stepsTransitionId = ref('');
const stepsTitle = ref('');
const steps = ref<TransitionStep[]>([]);
const stepsLoading = ref(false);

const stepPopupOpen = ref(false);
const stepMode = ref<'create' | 'edit'>('create');
const stepForm = ref<SendSmsStepFormState>(defaultSendSmsStep());

const hasProject = computed(() => Boolean(props.projectId?.trim()));
const systemId = computed(() => project.value?.system_id ?? '');

async function loadGoals() {
  try {
    const goals = await fetchGoals();
    goalItems.value = goals.map((g) => g.goal).filter(Boolean);
  } catch {
    goalItems.value = [];
  }
}

function goalSelectItems(current: string): string[] {
  const items = [...goalItems.value];
  if (current && !items.includes(current)) items.unshift(current);
  return items;
}

async function loadProject() {
  const id = props.projectId?.trim();
  if (!id) {
    project.value = null;
    return;
  }
  try {
    project.value = await projectsAdapter.byKey?.(id) ?? null;
  } catch (e) {
    project.value = null;
    showError(e);
  }
}

async function loadTransitions() {
  const id = props.projectId?.trim();
  if (!id) {
    transitions.value = [];
    return;
  }
  loading.value = true;
  try {
    transitions.value = await listTransitions(id, true);
  } catch (e) {
    showError(e);
    transitions.value = [];
  } finally {
    loading.value = false;
  }
}

function openCreateTransition() {
  transitionMode.value = 'create';
  transitionForm.value = { id: '', src_goal: '', dst_goal: '', schedule_interval: 60 };
  transitionPopupOpen.value = true;
}

function openEditTransition(row: Transition) {
  transitionMode.value = 'edit';
  transitionForm.value = {
    id: row.id,
    src_goal: row.src_goal,
    dst_goal: row.dst_goal,
    schedule_interval: row.schedule_interval,
  };
  transitionPopupOpen.value = true;
}

async function saveTransition() {
  const id = props.projectId?.trim();
  if (!id) return;
  const src = transitionForm.value.src_goal.trim();
  const dst = transitionForm.value.dst_goal.trim();
  if (!src || !dst) {
    showError('Укажите src_goal и dst_goal');
    return;
  }
  try {
    if (transitionMode.value === 'create') {
      await createTransition(id, {
        src_goal: src,
        dst_goal: dst,
        schedule_interval: transitionForm.value.schedule_interval,
      });
      showSuccess('Transition создан');
    } else {
      await updateTransition(id, transitionForm.value.id, {
        src_goal: src,
        dst_goal: dst,
        schedule_interval: transitionForm.value.schedule_interval,
      });
      showSuccess('Сохранено');
    }
    transitionPopupOpen.value = false;
    await loadTransitions();
  } catch (e) {
    showError(e);
  }
}

async function removeTransition(row: Transition) {
  const id = props.projectId?.trim();
  if (!id) return;
  if (!window.confirm(`Удалить transition ${row.src_goal} → ${row.dst_goal}?`)) return;
  try {
    await deleteTransition(id, row.id);
    showSuccess('Удалено');
    await loadTransitions();
  } catch (e) {
    showError(e);
  }
}

async function openSteps(row: Transition) {
  stepsTransitionId.value = row.id;
  stepsTitle.value = `${row.src_goal} → ${row.dst_goal}`;
  stepsOpen.value = true;
  await reloadSteps();
}

async function reloadSteps() {
  const id = props.projectId?.trim();
  if (!id || !stepsTransitionId.value) return;
  stepsLoading.value = true;
  try {
    steps.value = await listTransitionSteps(id, stepsTransitionId.value);
  } catch (e) {
    showError(e);
  } finally {
    stepsLoading.value = false;
  }
}

function openCreateStep() {
  stepMode.value = 'create';
  stepForm.value = defaultSendSmsStep({
    stepOrder: steps.value.length + 1,
    senderName: project.value?.sender_name ?? '',
  });
  stepPopupOpen.value = true;
}

function openEditStep(row: TransitionStep) {
  stepMode.value = 'edit';
  stepForm.value = parseSendSmsStep(row);
  stepPopupOpen.value = true;
}

async function saveStep() {
  const id = props.projectId?.trim();
  if (!id) return;
  try {
    const payload = toStepPayload(stepForm.value, systemId.value);
    if (stepMode.value === 'create') {
      await createTransitionStep(id, stepsTransitionId.value, payload);
      showSuccess('Шаг добавлен');
    } else {
      await updateTransitionStep(id, stepsTransitionId.value, stepForm.value.id, payload);
      showSuccess('Сохранено');
    }
    stepPopupOpen.value = false;
    await reloadSteps();
  } catch (e) {
    showError(e);
  }
}

async function removeStep(row: TransitionStep) {
  const id = props.projectId?.trim();
  if (!id) return;
  if (!window.confirm('Удалить шаг?')) return;
  try {
    await deleteTransitionStep(id, stepsTransitionId.value, row.id);
    await reloadSteps();
    showSuccess('Удалено');
  } catch (e) {
    showError(e);
  }
}

function onGoalCustom(field: 'src_goal' | 'dst_goal', e: { text?: string; customItem?: unknown }) {
  const value = (e.text ?? '').trim();
  e.customItem = value || null;
  if (value) transitionForm.value[field] = value;
}

watch(
  () => props.projectId,
  async () => {
    stepsOpen.value = false;
    stepPopupOpen.value = false;
    transitionPopupOpen.value = false;
    await Promise.all([loadProject(), loadTransitions()]);
  },
  { immediate: true },
);

onMounted(() => {
  void loadGoals();
});
</script>

<template>
  <div class="transitions-editor">
    <div v-if="!hasProject" class="transitions-editor__empty">
      Выберите проект CDP, чтобы загрузить transitions.
    </div>
    <template v-else>
      <div class="transitions-editor__toolbar">
        <h3>Transitions</h3>
        <div class="transitions-editor__actions">
          <DxButton text="Добавить правило" type="default" @click="openCreateTransition" />
          <DxButton text="Обновить" styling-mode="outlined" @click="loadTransitions" />
        </div>
      </div>
      <DxLoadPanel :visible="loading" :shading="true" />
      <DxDataGrid
        :data-source="transitions"
        :show-borders="false"
        :show-row-lines="false"
        :show-column-lines="false"
        :row-alternation-enabled="true"
        :column-auto-width="true"
        :hover-state-enabled="true"
        :paging="paging"
        :pager="pager"
        key-expr="id"
        height="480"
        width="100%"
      >
        <DxScrolling mode="standard" />
        <DxColumn data-field="src_goal" caption="Src goal" />
        <DxColumn data-field="dst_goal" caption="Dst goal" />
        <DxColumn data-field="schedule_interval" caption="Interval (с)" width="120" />
        <DxColumn type="buttons" :width="160">
          <DxGridButton hint="Шаги" icon="orderedlist" :on-click="(e: any) => openSteps(e.row.data)" />
          <DxGridButton hint="Изменить" icon="edit" :on-click="(e: any) => openEditTransition(e.row.data)" />
          <DxGridButton hint="Удалить" icon="trash" :on-click="(e: any) => removeTransition(e.row.data)" />
        </DxColumn>
      </DxDataGrid>
    </template>

    <DxPopup
      v-model:visible="transitionPopupOpen"
      :title="transitionMode === 'create' ? 'Новый transition' : 'Transition'"
      :width="480"
      :height="360"
    >
      <template #content>
        <div class="tf">
          <div class="tf__field">
            <label>Src goal</label>
            <DxSelectBox
              :value="transitionForm.src_goal"
              :items="goalSelectItems(transitionForm.src_goal)"
              :search-enabled="true"
              :accept-custom-value="true"
              placeholder="click"
              @customItemCreating="(e: any) => onGoalCustom('src_goal', e)"
              @value-changed="(e: any) => (transitionForm.src_goal = String(e.value ?? ''))"
            />
          </div>
          <div class="tf__field">
            <label>Dst goal</label>
            <DxSelectBox
              :value="transitionForm.dst_goal"
              :items="goalSelectItems(transitionForm.dst_goal)"
              :search-enabled="true"
              :accept-custom-value="true"
              placeholder="lead"
              @customItemCreating="(e: any) => onGoalCustom('dst_goal', e)"
              @value-changed="(e: any) => (transitionForm.dst_goal = String(e.value ?? ''))"
            />
          </div>
          <div class="tf__field">
            <label>Schedule interval, сек</label>
            <DxNumberBox
              :value="transitionForm.schedule_interval"
              :min="1"
              @value-changed="(e: any) => (transitionForm.schedule_interval = Number(e.value) || 60)"
            />
          </div>
        </div>
      </template>
      <DxToolbarItem
        widget="dxButton"
        toolbar="bottom"
        location="after"
        :options="{ text: 'Сохранить', type: 'default', onClick: saveTransition }"
      />
      <DxToolbarItem
        widget="dxButton"
        toolbar="bottom"
        location="after"
        :options="{ text: 'Отмена', onClick: () => (transitionPopupOpen = false) }"
      />
    </DxPopup>

    <DxPopup
      v-model:visible="stepsOpen"
      :title="`Шаги: ${stepsTitle}`"
      :width="900"
      :height="560"
    >
      <template #content>
        <div class="transitions-editor__toolbar">
          <DxButton text="Добавить шаг" type="default" @click="openCreateStep" />
        </div>
        <DxLoadPanel :visible="stepsLoading" :shading="true" />
        <DxDataGrid
          :data-source="steps"
          :show-borders="true"
          :paging="paging"
          :pager="pager"
          key-expr="id"
          height="380"
        >
          <DxScrolling mode="standard" />
          <DxColumn data-field="step_order" caption="№" width="60" />
          <DxColumn data-field="timeout" caption="Timeout (с)" width="110" />
          <DxColumn
            caption="Settings"
            :calculate-cell-value="formatStepSettings"
            :allow-sorting="false"
          />
          <DxColumn type="buttons" :width="100">
            <DxGridButton icon="edit" :on-click="(e: any) => openEditStep(e.row.data)" />
            <DxGridButton icon="trash" :on-click="(e: any) => removeStep(e.row.data)" />
          </DxColumn>
        </DxDataGrid>
      </template>
      <DxToolbarItem
        widget="dxButton"
        toolbar="bottom"
        location="after"
        :options="{ text: 'Закрыть', onClick: () => (stepsOpen = false) }"
      />
    </DxPopup>

    <DxPopup
      v-model:visible="stepPopupOpen"
      :title="stepMode === 'create' ? 'Новый шаг' : 'Шаг'"
      :width="640"
      :height="620"
    >
      <template #content>
        <SendSmsStepForm v-model="stepForm" />
      </template>
      <DxToolbarItem
        widget="dxButton"
        toolbar="bottom"
        location="after"
        :options="{ text: 'Сохранить', type: 'default', onClick: saveStep }"
      />
      <DxToolbarItem
        widget="dxButton"
        toolbar="bottom"
        location="after"
        :options="{ text: 'Отмена', onClick: () => (stepPopupOpen = false) }"
      />
    </DxPopup>
  </div>
</template>

<style scoped>
.transitions-editor {
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-height: 0;
}

.transitions-editor__empty {
  padding: 12px 0;
  color: var(--dx-color-text-secondary, #666);
  font-size: 0.875rem;
}

.transitions-editor__toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.transitions-editor__toolbar h3 {
  margin: 0;
  font-size: 1rem;
}

.transitions-editor__actions {
  display: flex;
  gap: 8px;
}

.tf {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.tf__field {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.tf__field label {
  font-size: 12px;
  color: var(--dx-color-text-secondary, #666);
}
</style>
